// Context for sharing the current active manuscript across components
import { createContext, useContext, useState, useCallback, useMemo, type ReactNode, type RefObject } from "react";

export interface ScenePath {
  actTitle: string;
  chapterTitle: string;
  sceneTitle: string;
}

interface ManuscriptContextValue {
  manuscriptId: string | null;
  setManuscriptId: (id: string | null) => void;
  activeSceneId: string | null;
  setActiveSceneId: (id: string | null) => void;
  activeChapterId: string | null;
  setActiveChapterId: (id: string | null) => void;
  viewMode: "scene" | "chapter";
  setViewMode: (mode: "scene" | "chapter") => void;
  activeScenePath: ScenePath | null;
  setActiveScenePath: (path: ScenePath | null) => void;
  editorRef: RefObject<HTMLDivElement> | null;
  registerEditorRef: (ref: RefObject<HTMLDivElement>) => void;
  triggerEditorSave: (() => Promise<void>) | null;
  registerTriggerSave: (fn: () => Promise<void>) => void;
  plannerMode: "navigate" | "plan" | "structure";
  setPlannerMode: (mode: "navigate" | "plan" | "structure") => void;
  activePlanItemId: string | null;
  setActivePlanItemId: (id: string | null) => void;
  activePlanItemType: "act" | "chapter" | "scene" | null;
  setActivePlanItemType: (type: "act" | "chapter" | "scene" | null) => void;
  selectedStructureItemIds: string[];
  setSelectedStructureItemIds: (ids: string[]) => void;
  activePopupCodexEntryId: string | null;
  setActivePopupCodexEntryId: (id: string | null) => void;
}

const ManuscriptContext = createContext<ManuscriptContextValue | undefined>(undefined);

interface ManuscriptProviderProps {
  children: ReactNode;
}

export function ManuscriptProvider({ children }: ManuscriptProviderProps) {
  const [manuscriptId, setManuscriptIdState] = useState<string | null>(null);
  const [activeSceneId, setActiveSceneIdState] = useState<string | null>(null);
  const [activeChapterId, setActiveChapterIdState] = useState<string | null>(null);
  const [viewMode, setViewModeState] = useState<"scene" | "chapter">("scene");
  const [activeScenePath, setActiveScenePathState] = useState<ScenePath | null>(null);
  // Use state instead of refs so consumers re-render when these are registered
  const [editorRef, setEditorRef] = useState<RefObject<HTMLDivElement> | null>(null);
  const [triggerEditorSave, setTriggerEditorSave] = useState<(() => Promise<void>) | null>(null);

  const [plannerMode, setPlannerModeState] = useState<"navigate" | "plan" | "structure">("navigate");
  const [activePlanItemId, setActivePlanItemId] = useState<string | null>(null);
  const [activePlanItemType, setActivePlanItemType] = useState<"act" | "chapter" | "scene" | null>(null);
  const [selectedStructureItemIds, setSelectedStructureItemIds] = useState<string[]>([]);
  const [activePopupCodexEntryId, setActivePopupCodexEntryId] = useState<string | null>(null);

  const setPlannerMode = useCallback((mode: "navigate" | "plan" | "structure") => {
    setPlannerModeState(mode);
    setSelectedStructureItemIds([]);
  }, []);

  const setManuscriptId = useCallback((id: string | null) => {
    setManuscriptIdState(id);
    if (!id) {
      setActiveSceneIdState(null);
      setActiveChapterIdState(null);
      setViewModeState("scene");
      setActiveScenePathState(null);
      setPlannerModeState("navigate");
      setActivePlanItemId(null);
      setActivePlanItemType(null);
      setSelectedStructureItemIds([]);
      setActivePopupCodexEntryId(null);
    }
  }, []);

  const setActiveSceneId = useCallback((id: string | null) => {
    setActiveSceneIdState(id);
  }, []);

  const setActiveChapterId = useCallback((id: string | null) => {
    setActiveChapterIdState(id);
  }, []);

  const setViewMode = useCallback((mode: "scene" | "chapter") => {
    setViewModeState(mode);
  }, []);

  const setActiveScenePath = useCallback((path: ScenePath | null) => {
    setActiveScenePathState(path);
  }, []);

  const registerEditorRef = useCallback((ref: RefObject<HTMLDivElement>) => {
    setEditorRef(ref);
  }, []);

  const registerTriggerSave = useCallback((fn: () => Promise<void>) => {
    // Wrap in arrow to avoid setState treating fn as an updater
    setTriggerEditorSave(() => fn);
  }, []);

  const contextValue = useMemo(() => ({
    manuscriptId,
    setManuscriptId,
    activeSceneId,
    setActiveSceneId,
    activeChapterId,
    setActiveChapterId,
    viewMode,
    setViewMode,
    activeScenePath,
    setActiveScenePath,
    editorRef,
    registerEditorRef,
    triggerEditorSave,
    registerTriggerSave,
    plannerMode,
    setPlannerMode,
    activePlanItemId,
    setActivePlanItemId,
    activePlanItemType,
    setActivePlanItemType,
    selectedStructureItemIds,
    setSelectedStructureItemIds,
    activePopupCodexEntryId,
    setActivePopupCodexEntryId,
  }), [
    manuscriptId,
    setManuscriptId,
    activeSceneId,
    setActiveSceneId,
    activeChapterId,
    setActiveChapterId,
    viewMode,
    setViewMode,
    activeScenePath,
    setActiveScenePath,
    editorRef,
    registerEditorRef,
    triggerEditorSave,
    registerTriggerSave,
    plannerMode,
    setPlannerMode,
    activePlanItemId,
    activePlanItemType,
    selectedStructureItemIds,
    activePopupCodexEntryId,
  ]);

  return (
    <ManuscriptContext.Provider value={contextValue}>
      {children}
    </ManuscriptContext.Provider>
  );
}

export function useManuscriptContext(): ManuscriptContextValue {
  const context = useContext(ManuscriptContext);
  if (context === undefined) {
    throw new Error("useManuscriptContext must be used within a ManuscriptProvider");
  }
  return context;
}

// Hook that returns null if not in context (for optional usage)
export function useOptionalManuscriptContext(): ManuscriptContextValue | null {
  const context = useContext(ManuscriptContext);
  return context ?? null;
}
